import { useMemo, useState } from 'react';

const usePaging = <T>(
  items: T[],
  pageSize: number,
  defaultPage?: number,
): [T[], number, number, React.Dispatch<React.SetStateAction<number>>] => {
  const [page, setPage] = useState(defaultPage || 0);

  /**
   * Find actual page relative to items length.
   * Finds an actual page in the item set even if state page is above what is available.
   */
  const actualPage = useMemo(() => {
    const skip = page * pageSize;
    return skip > items.length ? Math.floor(items.length / pageSize) : page;
  }, [page, pageSize, items]);

  /**
   * Finds the items for the current actual page.
   */
  const pageItems = useMemo(() => {
    const skip = actualPage * pageSize;
    return items.slice(skip, skip + pageSize);
  }, [actualPage, pageSize, items]);
  /**
   * Finds the total number of pages.
   */
  const totalPages = useMemo(
    () => Math.ceil(items.length / pageSize),
    [items, pageSize],
  );

  /**
   * Changes to a specific page.
   */
  const changePage: React.Dispatch<React.SetStateAction<number>> = (
    value: React.SetStateAction<number>,
  ) => {
    const result = value instanceof Function ? value(page) : value;
    if (result >= 0 && result <= totalPages) setPage(result);
  };

  return [pageItems, actualPage, totalPages, changePage];
};
export default usePaging;
